import React, { useMemo } from "react";
import parse, {
  DOMNode,
  domToReact,
  Element,
  HTMLReactParserOptions,
} from "html-react-parser";
import { cn, isVoidElement } from "utils/presentation";
import {
  BRACKET_TOKEN,
  onePieceTokenAppearance,
  sanitizeOnePieceRulesText,
} from "modules/cards/presentation/cardRulesText";

function badgeForToken(token: string, key: string): React.ReactNode {
  const appearance = onePieceTokenAppearance(token);
  if (!appearance) return token;
  return (
    <span
      key={key}
      style={appearance.style}
      className={cn(
        "badge badge-sm mr-0.5 h-auto min-h-4 border-0 px-1.5 py-px align-baseline font-semibold not-italic shadow-none",
        appearance.className
      )}
    >
      {token.slice(1, -1)}
    </span>
  );
}

function badgeTokens(text: string, keyPrefix: string): React.ReactNode[] {
  return text
    .split(BRACKET_TOKEN)
    .map((part, index) =>
      part.startsWith("[") && part.endsWith("]")
        ? badgeForToken(part, `${keyPrefix}-${index}`)
        : part
    );
}

interface OnePieceRulesTextProps {
  text: string;
  className?: string;
}

export function OnePieceRulesText({ text, className }: OnePieceRulesTextProps) {
  const content = useMemo(() => {
    const sanitized = sanitizeOnePieceRulesText(text);
    if (!sanitized) return null;

    let keyCounter = 0;
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (domNode.type === "text") {
          return <>{badgeTokens(domNode.data, `t${keyCounter++}`)}</>;
        }
        if (domNode.type !== "tag") return undefined;

        const element = domNode as Element;

        // Void elements (a <br class=x> that survives SOURCE_BREAK) must not
        // receive children — React throws on self-closing tags with children.
        if (isVoidElement(element.name)) {
          return React.createElement(element.name, {
            key: `e-${keyCounter++}`,
          });
        }

        const children = element.children
          ? domToReact(element.children as DOMNode[], options)
          : undefined;

        // Errata links point at Bandai's official rules pages and are the only
        // anchors in this feed, so they keep their href — ReplacedSymbolsOracleText
        // drops all attributes and would render a dead anchor.
        if (element.name === "a") {
          return (
            <a
              key={`a-${keyCounter++}`}
              href={element.attribs?.href}
              className="link link-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          );
        }

        return React.createElement(
          element.name,
          { key: `e-${keyCounter++}` },
          children
        );
      },
    };

    return parse(sanitized, options);
  }, [text]);

  if (!content) return null;

  return (
    <p className={cn("text-base-content whitespace-pre-line", className)}>
      {content}
    </p>
  );
}
